import { useState } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db6g2djghk82orf0cl6g';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // honeypot: silently accept and stop for bots
    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.message ||
        responseText ||
        '';
      const isSpam = serverMsg.toLowerCase().includes('spam');
      const ok = response.ok && parsed?.code === 'OK';

      if (!ok || isSpam) {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
        return;
      }

      setStatus('success');
      form.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  if (status === 'success') {
    return (
      <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-7 text-center">
        <span className="mx-auto w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center">
          <i className="ri-checkbox-circle-fill text-accent-600 text-3xl"></i>
        </span>
        <h3 className="mt-4 font-display text-lg font-bold text-foreground-950">提交成功</h3>
        <p className="mt-2 text-sm text-foreground-500">
          感谢你的反馈，我们已收到你的留言，会尽快处理。
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-100 border border-background-300 hover:border-primary-400 text-sm text-foreground-800 transition-colors cursor-pointer"
        >
          <i className="ri-edit-line"></i>
          再写一条
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      id="about-feedback-form"
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-7"
    >
      <h3 className="font-display text-lg font-bold text-foreground-950">意见反馈</h3>
      <p className="mt-1.5 text-sm text-foreground-500">
        有任何建议或问题，欢迎给我们留言，我们会认真阅读每一条反馈。
      </p>

      <div className="mt-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="fb-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
              姓名
            </label>
            <input
              id="fb-name"
              name="name"
              type="text"
              required
              placeholder="请输入你的姓名"
              className="w-full h-11 px-3.5 rounded-md border border-background-300 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
            />
          </div>
          <div>
            <label htmlFor="fb-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
              邮箱
            </label>
            <input
              id="fb-email"
              name="email"
              type="email"
              required
              placeholder="请输入你的邮箱"
              className="w-full h-11 px-3.5 rounded-md border border-background-300 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
            />
          </div>
        </div>

        <div>
          <label htmlFor="fb-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
            留言内容
          </label>
          <textarea
            id="fb-message"
            name="message"
            required
            maxLength={500}
            rows={5}
            placeholder="请描述你的建议或遇到的问题（最多 500 字）"
            className="w-full px-3.5 py-3 rounded-md border border-background-300 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors resize-none"
          />
        </div>

        <input
          className="form-field-hidden"
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        {status === 'error' && (
          <div className="flex items-center gap-2 rounded-md bg-accent-50 border border-accent-200 px-3.5 py-2.5 text-sm text-accent-800">
            <i className="ri-error-warning-line"></i>
            {formError}
          </div>
        )}

        <button
          type="submit"
          disabled={status === 'submitting'}
          className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
            status === 'submitting'
              ? 'bg-primary-300 text-background-50 cursor-not-allowed'
              : 'bg-primary-500 hover:bg-primary-600 text-background-50 cursor-pointer'
          }`}
        >
          {status === 'submitting' ? (
            <>
              <span className="w-4 h-4 rounded-full border-2 border-background-50/40 border-t-background-50 animate-spin"></span>
              提交中…
            </>
          ) : (
            <>
              <i className="ri-send-plane-line"></i>
              提交反馈
            </>
          )}
        </button>
      </div>
    </form>
  );
}